"use client";

import { useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import BottomSheetModal from "@/components/ui/BottomSheetModal/BottomSheetModal";

// Composant pour l'icône de team
function TeamIcon({ team, size = "md" }) {
    const sizeClasses = {
        sm: "w-8 h-8 text-xs rounded-lg",
        md: "w-10 h-10 text-sm rounded-lg",
    };

    const iconSize = sizeClasses[size] || sizeClasses.md;
    const [w, h, , rounded] = iconSize.split(" ");

    if (team.iconUrl) {
        return (
            <img
                src={team.iconUrl}
                alt={team.name}
                className={`${w} ${h} ${rounded} object-cover shadow-sm`}
            />
        );
    }

    return (
        <div className={`${w} ${h} ${rounded} bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-sm`}>
            <span className={`text-white font-semibold ${iconSize.split(" ")[2] || "text-sm"}`}>
                {team.name?.[0]?.toUpperCase() || "?"}
            </span>
        </div>
    );
}

export function ManageTeamsModal({ isOpen, onClose, teams = [], currentTeamId }) {
    const t = useTranslations("layout");
    const router = useRouter();
    const [teamToLeave, setTeamToLeave] = useState(null);
    const [confirmationText, setConfirmationText] = useState("");
    const [isLeaving, setIsLeaving] = useState(false);
    const [error, setError] = useState(null);

    const handleLeaveClick = (team) => {
        setTeamToLeave(team);
        setConfirmationText("");
        setError(null);
    };

    const handleCancelLeave = () => {
        setTeamToLeave(null);
        setConfirmationText("");
        setError(null);
    };

    const handleConfirmLeave = async () => {
        if (!teamToLeave || confirmationText !== teamToLeave.name) {
            return;
        }

        setIsLeaving(true);
        setError(null);

        try {
            const response = await fetch(`/api/teams/${teamToLeave.id}/leave`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.message || t("manageTeams.leaveError"));
                setIsLeaving(false);
                return;
            }

            // Si on quitte l'équipe actuelle, rediriger
            if (teamToLeave.id === currentTeamId) {
                window.location.href = "/";
            } else {
                // Sinon, juste rafraîchir la page pour mettre à jour la liste
                window.location.reload();
            }
        } catch (err) {
            console.error("Erreur lors du départ de l'équipe:", err);
            setError(t("manageTeams.leaveError"));
            setIsLeaving(false);
        }
    };

    const canLeaveTeam = (team) => {
        return team.role !== "owner";
    };

    const isConfirmationValid = teamToLeave && confirmationText === teamToLeave.name;

    return (
        <BottomSheetModal
            isOpen={isOpen}
            onClose={teamToLeave ? undefined : onClose}
            maxWidthClass="sm:max-w-md"
            zIndexClass="z-[9999]"
        >
            <div>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-grayDark-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-grayDark-100">
                        {teamToLeave ? t("manageTeams.confirmLeaveTitle") : t("manageTeams.title")}
                    </h3>
                    <button
                        onClick={teamToLeave ? handleCancelLeave : onClose}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-grayDark-700 transition-colors"
                    >
                        <SolarIcon name="close-circle-linear" className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                {!teamToLeave ? (
                    // Liste des équipes
                    <div className="p-4 max-h-96 overflow-y-auto">
                        <p className="text-sm text-gray-500 dark:text-grayDark-400 mb-4">
                            {t("manageTeams.description")}
                        </p>

                        <div className="space-y-2">
                            {teams.map((team) => (
                                <div
                                    key={team.id}
                                    className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-grayDark-700/50 border border-gray-100 dark:border-grayDark-600"
                                >
                                    <div className="flex items-center gap-3">
                                        <TeamIcon team={team} size="md" />
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900 dark:text-grayDark-100 mb-0">
                                                {team.name}
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-grayDark-400 mb-0">
                                                {team.role === "owner"
                                                    ? t("teamSwitcher.roleOwner")
                                                    : team.role === "manager"
                                                    ? t("teamSwitcher.roleManager")
                                                    : t("teamSwitcher.roleMember")}
                                            </p>
                                        </div>
                                    </div>

                                    {canLeaveTeam(team) ? (
                                        <button
                                            onClick={() => handleLeaveClick(team)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <SolarIcon name="logout-2-linear" className="w-4 h-4" />
                                            {t("manageTeams.leave")}
                                        </button>
                                    ) : (
                                        <span className="text-xs text-gray-400 dark:text-grayDark-500 italic">
                                            {t("manageTeams.ownerCannotLeave")}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    // Confirmation de départ
                    <div className="p-4">
                        <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-xl mb-4">
                            <SolarIcon name="danger-triangle-linear" className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                                    {t("manageTeams.warningTitle")}
                                </p>
                                <p className="text-sm text-red-600 dark:text-red-400 mb-0">
                                    {t("manageTeams.warningDescription")}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-grayDark-700/50 rounded-xl mb-4">
                            <TeamIcon team={teamToLeave} size="sm" />
                            <p className="text-sm font-semibold text-gray-900 dark:text-grayDark-100 mb-0">
                                {teamToLeave.name}
                            </p>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-grayDark-300 mb-2">
                                {t("manageTeams.confirmLabel", { teamName: teamToLeave.name })}
                            </label>
                            <input
                                type="text"
                                value={confirmationText}
                                onChange={(e) => setConfirmationText(e.target.value)}
                                placeholder={teamToLeave.name}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-grayDark-600 rounded-lg bg-white dark:bg-grayDark-700 text-gray-900 dark:text-grayDark-100 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                                disabled={isLeaving}
                            />
                        </div>

                        {error && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                                <p className="text-sm text-red-600 dark:text-red-400 mb-0">{error}</p>
                            </div>
                        )}

                        <div className="flex gap-3">
                            <button
                                onClick={handleCancelLeave}
                                disabled={isLeaving}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-grayDark-300 bg-gray-100 dark:bg-grayDark-700 hover:bg-gray-200 dark:hover:bg-grayDark-600 rounded-lg transition-colors disabled:opacity-50 btn-3d"
                            >
                                {t("manageTeams.cancel")}
                            </button>
                            <button
                                onClick={handleConfirmLeave}
                                disabled={!isConfirmationValid || isLeaving}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed btn-3d"
                            >
                                {isLeaving ? (
                                    <>
                                        <SolarIcon name="refresh-linear" className="w-4 h-4 animate-spin" />
                                        {t("manageTeams.leaving")}
                                    </>
                                ) : (
                                    <>
                                        <SolarIcon name="logout-2-linear" className="w-4 h-4" />
                                        {t("manageTeams.confirmLeave")}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </BottomSheetModal>
    );
}
